import { DOCUMENT } from '@angular/common';
import { Component, HostListener, Inject, OnDestroy } from '@angular/core';
import {
  Event,
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  NavigationSkipped,
} from '@angular/router';
import { combineLatest, filter, map, Observable, startWith, Subscription } from 'rxjs';
import { PermissionsService } from '@shared/permissions/permissions.service';
import { BuildVersionService } from './shared/services/build-version.service';
import { environment } from '../environments/environment';
import { AuthService } from 'src/@core/services/auth.service';
import { ViewportGuidanceService } from './shared/services/viewport-guidance.service';
import { CommandPaletteService } from './shared/command-palette/command-palette.service';
import { MatIconRegistry } from '@angular/material/icon';
import { GoogleAnalyticsService } from './shared/services/google-analytics.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnDestroy {
  private readonly spinnerSafetyTimeoutMs = 12000;
  private routerEventsSub?: Subscription;
  private spinnerSafetyTimer: ReturnType<typeof setTimeout> | null = null;
  chatAvailable$: Observable<boolean>;
  pageLoading = false;

  currentUrl: string;

  constructor(
    public _router: Router,
    private permissionsService: PermissionsService,
    private buildVersionService: BuildVersionService,
    private authService: AuthService,
    private viewportGuidanceService: ViewportGuidanceService,
    private commandPaletteService: CommandPaletteService,
    private matIconRegistry: MatIconRegistry,
    private googleAnalyticsService: GoogleAnalyticsService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.matIconRegistry.setDefaultFontSetClass('material-symbols-outlined');
    this.googleAnalyticsService.initialize();
    this.chatAvailable$ = combineLatest([
      this.authService.loggedInUser$,
      this._router.events.pipe(
        filter((routerEvent): routerEvent is NavigationEnd => routerEvent instanceof NavigationEnd),
        map((routerEvent) => routerEvent.urlAfterRedirects || routerEvent.url),
        startWith(this.getCurrentNavigationUrl())
      )
    ]).pipe(
      map(([user, currentUrl]) => this.isAuthenticatedUser(user) && this.showChatUi(currentUrl))
    );

    this.routerEventsSub = this._router.events.subscribe((routerEvent: Event) => {
      if (routerEvent instanceof NavigationStart) {
        const routeUrl = routerEvent.url || '';
        const isRootRoute = routeUrl === '/' || routeUrl === '';
        const isAuthenticationRoute = routeUrl.startsWith('/authentication');
        const isLaunchpadSelfNavigation = this.normalizePath(routeUrl).startsWith('/launchpad')
          && this.normalizePath(this.getCurrentNavigationUrl()).startsWith('/launchpad');
        if (!isRootRoute && !isAuthenticationRoute && !isLaunchpadSelfNavigation) {
          this.pageLoading = true;
          this.startSpinnerSafetyTimer();
        } else {
          this.hideSpinnerSafely();
        }
        this.currentUrl = routerEvent.url.substring(
          routerEvent.url.lastIndexOf('/') + 1
        );
      }
      if (
        routerEvent instanceof NavigationEnd ||
        routerEvent instanceof NavigationCancel ||
        routerEvent instanceof NavigationError ||
        routerEvent instanceof NavigationSkipped
      ) {
        if (
          routerEvent instanceof NavigationEnd &&
          this.buildVersionService.isEnabled()
        ) {
          this.buildVersionService.checkAndReloadIfVersionChanged('http');
        }
        if (routerEvent instanceof NavigationEnd) {
          this.googleAnalyticsService.trackPageView(
            routerEvent.urlAfterRedirects || routerEvent.url
          );
          this.viewportGuidanceService.onRouteChanged(
            routerEvent.urlAfterRedirects || routerEvent.url
          );
        }
        this.hideSpinnerSafely();
      }
      window.scrollTo(0, 0);
    });
  }

  ngOnInit() {
    if (this.buildVersionService.isEnabled()) {
      this.buildVersionService.initialize();
      this.buildVersionService.checkAndReloadIfVersionChanged('startup');
    }
    this.applyStoredThemePreference();
    this.applyEnvironmentFrame();
    this.loadPermissions();
    this.viewportGuidanceService.start();
    this.viewportGuidanceService.onRouteChanged(this._router.url);
  }

  ngOnDestroy(): void {
    this.routerEventsSub?.unsubscribe();
    this.clearSpinnerSafetyTimer();
    this.viewportGuidanceService.stop();
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.commandPaletteService.open();
    }
  }

  loadPermissions() {
    const user = this.authService.getCurrentUser();
    if (user) {
      this.permissionsService.loadUserAccess(user);
    }
  }

  private startSpinnerSafetyTimer(): void {
    this.clearSpinnerSafetyTimer();
    this.spinnerSafetyTimer = setTimeout(() => {
      // Fail-safe: prevent full-screen loader from blocking the app indefinitely
      // if navigation events fail to resolve in edge/runtime-error paths.
      this.hideSpinnerSafely();
    }, this.spinnerSafetyTimeoutMs);
  }

  private clearSpinnerSafetyTimer(): void {
    if (this.spinnerSafetyTimer) {
      clearTimeout(this.spinnerSafetyTimer);
      this.spinnerSafetyTimer = null;
    }
  }

  private hideSpinnerSafely(): void {
    this.clearSpinnerSafetyTimer();
    this.pageLoading = false;
  }

  private applyStoredThemePreference(): void {
    const savedTheme = localStorage.getItem('theme');
    const effectiveTheme = savedTheme === 'dark' || savedTheme === 'light' ? savedTheme : 'light';
    if (!savedTheme) {
      localStorage.setItem('theme', effectiveTheme);
    }
    const shouldUseDarkTheme = effectiveTheme === 'dark';
    this.document.body.classList.toggle('dark-theme', shouldUseDarkTheme);
    this.document.body.classList.toggle('dark', shouldUseDarkTheme);
  }

  private applyEnvironmentFrame(): void {
    this.document.body.classList.toggle('development-environment-frame', !environment.production);
  }

  showMainHeader(): boolean {
    const currentPath = this.normalizePath(this._router.url);
    if (this.isPublicLandingRoute(currentPath) || this.isPublicVerificationRoute(currentPath)) {
      return false;
    }

    return !currentPath.startsWith('/authentication');
  }

  private isPublicLandingRoute(path: string): boolean {
    return path === '' || path === '/' || path === '/index' || path === '/forlab' || path === '/forlab/index';
  }

  private isPublicVerificationRoute(path: string): boolean {
    return path.startsWith('/verify/');
  }

  private normalizePath(url: string): string {
    const rawUrl = String(url || '');
    const hashRouteIndex = rawUrl.indexOf('#/');
    const routeLikeUrl = hashRouteIndex >= 0
      ? rawUrl.substring(hashRouteIndex + 1)
      : rawUrl.startsWith('#')
        ? rawUrl.substring(1)
        : rawUrl;
    return (routeLikeUrl || '/').split('?')[0].split('#')[0];
  }

  private isAuthenticatedUser(user: any): boolean {
    return !!(user?.token && !user?.is_token_expired);
  }

  private showChatUi(url: string): boolean {
    return !this.isPublicVerificationRoute(this.normalizePath(url));
  }

  private getCurrentNavigationUrl(): string {
    const hash = this.document?.location?.hash || '';
    if (hash.startsWith('#/')) {
      return hash.substring(1);
    }

    return this._router.url || this.document?.location?.pathname || '/';
  }
}
